import { skipToken } from "@reduxjs/toolkit/query";
import { OptionData } from "components/DesignSystem/Combobox/Combobox";
import { useMemo } from "react";
import {
  ChurnDetailFormValues,
  ChurnDetailResponse,
  useGetChurnDetailDraftQuery,
  useGetChurnDetailPrefillQuery,
  useSubmitChurnDetailMutation,
} from "store/apis/salesCrm";
import { mergeUniqueValues } from "util/mergeUniqueValues";

export type ChurnDetailsFormValues = ChurnDetailFormValues & {
  request_spoc_to_fill: boolean;
  other_reason_detail: string;
};

const COMPLETED_STATUS = "COMPLETED";

const DEFAULT_FORM_VALUES: ChurnDetailsFormValues = {
  customer_name: "",
  spoc_name: [],
  joining_date: "",
  churn_reason: "",
  save_attempt_performed: "",
  escalations_involved: "",
  save_probability: "",
  csm_score: "",
  other_notes: "",
  company_dissolved: "",
  products_subscribed: [],
  filings_status: [],
  total_revenue_contribution: "",
  considered_churn: "",
  request_spoc_to_fill: false,
  other_reason_detail: "",
};

// Company Details fields (read-only, auto-filled) and the delegation flag itself are
// never cleared when "Request AM to fill in the details" is toggled on -- every other
// field is, so the AM starts from a blank churn-detail form.
const NON_CLEARABLE_FIELDS = new Set<keyof ChurnDetailsFormValues>([
  "customer_name",
  "spoc_name",
  "joining_date",
  "total_revenue_contribution",
  "products_subscribed",
  "filings_status",
  "request_spoc_to_fill",
]);

// Every field left after excluding NON_CLEARABLE_FIELDS holds a string value -- this
// mapped type captures that so callers can write "" through the key without TypeScript
// widening the write-through type to `never` (the write type for a plain `keyof` is the
// intersection of every field's type, which is empty once string/string[]/boolean mix).
type EditableStringField = {
  [K in keyof ChurnDetailsFormValues]: ChurnDetailsFormValues[K] extends string
    ? K
    : never;
}[keyof ChurnDetailsFormValues];

const isEditableStringField = (
  field: keyof ChurnDetailsFormValues
): field is EditableStringField => !NON_CLEARABLE_FIELDS.has(field);

export const CHURN_DETAIL_EDITABLE_FIELDS = (
  Object.keys(DEFAULT_FORM_VALUES) as (keyof ChurnDetailsFormValues)[]
).filter(isEditableStringField);

// Company Details fields are auto-filled from the company record and are never
// user-editable, so their multi-select options are just the auto-filled values
// themselves, self-labeled -- there is no larger catalog to choose from.
const toOption = (value: string): OptionData => ({ value, label: value });

// A stable reference so the "no value yet" fallback doesn't defeat the initialValues
// memo below with a fresh empty array on every render.
const EMPTY_LIST: string[] = [];

type UseChurnDetailsFormDataResult = {
  isLoadingExistingData: boolean;
  hasLoadError: boolean;
  initialValues: ChurnDetailsFormValues;
  productOptions: OptionData[];
  submitChurnDetail: ReturnType<typeof useSubmitChurnDetailMutation>[0];
  isSubmitting: boolean;
  isReviewMode: boolean;
};

export const useChurnDetailsFormData = (
  isOpen: boolean,
  groupId: string,
  draftId?: string,
  prefetchedDraft?: ChurnDetailResponse
): UseChurnDetailsFormDataResult => {
  // Company Details/option-catalog fields only matter for a brand-new submission --
  // once a draftId is set, every value comes from that record's own snapshot instead.
  const {
    data: prefillData,
    isFetching: isPrefillLoading,
    isError: isPrefillError,
  } = useGetChurnDetailPrefillQuery(
    { groupId },
    { skip: !isOpen || Boolean(draftId) }
  );

  const {
    data: fetchedDraftData,
    isFetching: isDraftLoading,
    isError: isDraftError,
  } = useGetChurnDetailDraftQuery(
    isOpen && draftId && !prefetchedDraft ? { groupId, draftId } : skipToken
  );

  const [submitChurnDetail, { isLoading: isSubmitting }] =
    useSubmitChurnDetailMutation();

  // A caller that already fetched the record (e.g. ReviewChurnDetailsButton) passes it
  // in directly so this hook doesn't re-fetch the exact same draft over the network.
  const draftData = prefetchedDraft ?? fetchedDraftData;
  const isLoadingExistingData = draftId
    ? !prefetchedDraft && isDraftLoading
    : isPrefillLoading;
  const hasLoadError = draftId
    ? !prefetchedDraft && isDraftError
    : isPrefillError;

  const scalarSource = draftId ? draftData : prefillData;
  // A draftId pointing at an already-COMPLETED record means the caller opened this
  // for review/edit (e.g. from the Archived page), not to complete a pending draft.
  const isReviewMode = draftData?.status === COMPLETED_STATUS;

  const pickListValue = (
    field: "products_subscribed" | "filings_status" | "spoc_name"
  ): string[] =>
    (draftId ? draftData?.[field] : prefillData?.[field]) ?? EMPTY_LIST;

  const selectedProducts = pickListValue("products_subscribed");
  const selectedFilings = pickListValue("filings_status");
  const selectedSpocValues = pickListValue("spoc_name");

  // Options include both the group's current catalog and whatever was selected when
  // this record was created, so a value that's since fallen out of the current catalog
  // still renders as a chip instead of silently vanishing from the display.
  const productOptions = mergeUniqueValues(
    prefillData?.products_subscribed ?? [],
    draftData?.products_subscribed ?? []
  ).map(toOption);

  const initialValues: ChurnDetailsFormValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
      ...scalarSource,
      products_subscribed: selectedProducts,
      filings_status: selectedFilings,
      spoc_name: selectedSpocValues,
      // Always start unset, regardless of what's stored on the record -- whoever has
      // the form open right now is the one about to fill it in (or delegate it fresh).
      request_spoc_to_fill: false,
    }),
    [scalarSource, selectedProducts, selectedFilings, selectedSpocValues]
  );

  return {
    isLoadingExistingData,
    hasLoadError,
    initialValues,
    productOptions,
    submitChurnDetail,
    isSubmitting,
    isReviewMode,
  };
};
